import axios from '@/clientAPI.ts'
import axiosV3 from '@/clientAPIV3.ts'
import {
    AccessToken,
    AttachSocialUser,
    AuthEmailPayload,
    AuthEmailVerifyResponseType,
    AuthState,
    FeaturedCreatorsRequestType,
    FeaturedFollowersRequestType,
    GenderResponse,
    GenderResponseV3,
    LoginPayload,
    LoginSocialUserRequest,
    ProgressLoadingVideoTypes,
    SignupGuestPayloadV3,
    SignupHostPayloadV3,
    SignupRepresentativePayload,
    UploadVideoRequest,
    User,
    UserV3
} from '@/features/authentication/types.ts'
import {CountryType} from '@/features/country/types'
import {AxiosResponse} from 'axios'
import {GenderResponseSchema, LoginResponseV3, UserMeSchema} from './auth.schema'

export const AUTH_API = {
    login: async (values: LoginPayload): Promise<AuthState> => {
        const {data} = await axios.post('/auth/login', values)
        return data
    },
    logout: (): Promise<void> => {
        return axios.get('/auth/logout')
    },
    me: async (): Promise<UserV3> => {
        //TODO BE get avatar null is it possible?
        //TODO get social iterated object instead of array
        const {data} = await axiosV3.get('/profile')
        // const {data} = await axios.get('/profile')
        return UserMeSchema.parse({userType: data.type, data}).data
    },
    signup: (
        data: SignupHostPayloadV3 | SignupGuestPayloadV3 | SignupRepresentativePayload,
        queryParams?: Record<string, string>
    ): Promise<AxiosResponse<User>> => {
        let url = '/auth/signup'
        if (queryParams) {
            const queryString = new URLSearchParams(queryParams).toString()
            url = `${url}?${queryString}`
        }
        return axiosV3.post(url, data)
    },
    activateAccount: async (token: string): Promise<AuthState> => {
        const url = `/auth/signup/activate/${token}`
        const {data} = await axios.get(url, {params: {is_morgi: 1}})
        return data
    },
    loginSocialUser: async ({type, authResponse}: LoginSocialUserRequest): Promise<AxiosResponse<AccessToken>> => {
        const data = await axios.post(`/auth/signup/${type}`, authResponse)
        return data
    },
    getGenders: async (isLeader?: boolean): Promise<GenderResponse> => {
        const url = `/genders`
        const {data} = await axios.get(url, {params: {is_leader: isLeader}})
        return GenderResponseSchema.parse(data)
    },
    uploadVideo: async ({body, options}: UploadVideoRequest) => {
        const url = '/video'
        const {data} = await axios.post(url, body, {
            ...options,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        return data
    },
    checkStatusOfVideoFile: async ({
        jobId
    }: {
        jobId: string
    }): Promise<{
        percentage: number
        status: ProgressLoadingVideoTypes
        code: number
        file_path?: string
        error?: string
        video_tmp_url: string
    }> => {
        const {data} = await axios.get(`/video/status/${jobId}`)
        return data
    },
    getLocalize: async (): Promise<CountryType> => {
        const url = '/countries/localize'
        const {data} = await axios.get(url)
        return data
    },
    attachSocialUser: async ({
        userID,
        password,
        data,
        platform
    }: AttachSocialUser): Promise<// AccessToken
    AuthState> => {
        const url = `/auth/attach/${userID}/${platform}`
        const response = await axios.post(url, {password, ...data})
        return response.data
    },
    signupEmail: async (body: AuthEmailPayload, queryParams?: Record<string, string>): Promise<AuthState> => {
        let url = `auth`
        if (queryParams) {
            const queryString = new URLSearchParams(queryParams).toString()
            url = `${url}?${queryString}`
        }

        const {data} = await axiosV3.post(url, {...body})
        return data
    },
    loginEmail: async (email: string, queryParams?: Record<string, string>): Promise<AuthState> => {
        let url = `auth/emails/login`
        if (queryParams) {
            const queryString = new URLSearchParams(queryParams).toString()
            url = `${url}?${queryString}`
        }
        const {data} = await axios.post(url, {email})
        return data
    },
    verifyEmail: async (token: string): Promise<AuthEmailVerifyResponseType> => {
        const url = `auth/emails/verify/${token}`
        const {data} = await axios.get(url)
        return data
    },
    loginEmailVerification: async ({
        email,
        token,
        queryParams
    }: {
        email: string
        token: string
        queryParams?: Record<string, string>
    }): Promise<AuthState> => {
        let url = '/auth/login'
        if (queryParams) {
            const queryString = new URLSearchParams(queryParams).toString()
            url = `${url}?${queryString}`
        }
        const {data} = await axios.post(url, {email, auth_token: token})
        return data
    }
}

// API V3

export type QueryParamsType = Record<string, string>

export enum userTypes {
    ROOKIE = 'rookie',
    LEADER = 'leader',
    AGENT = 'agent',
    REPRESENTATIVE = 'representative',
    ADMIN = 'admin'
}

export type RequestMagicLinkBody = {
    email: string
    userRole: string
    specialType?: string
    lang: string
}

export type RequestMagicLink = {
    email: string
    userRole: Omit<userTypes, 'ADMIN'> // ADMIN is not allowed to sign up via magic link
    specialType?: 'SPORTS' | 'ARTS'
    authToken: string
}

export type SignupUserPayload = {
    authToken: string
    type: userTypes
    referralCode: string
    genderId: number
    publicGroup: number
    language: string
}

export type LoginUserPayload = {
    authToken: string
    rememberMe: boolean
}

export const requestMagicLink = async (
    body: RequestMagicLinkBody,
    queryParams?: QueryParamsType
): Promise<AuthState> => {
    let url = `auth`
    if (queryParams) {
        const queryString = new URLSearchParams(queryParams).toString()
        url = `${url}?${queryString}`
    }
    const {data} = await axiosV3.post(url, {...body})
    return data
}

export const signupUser = async (payload: SignupUserPayload, queryParams?: QueryParamsType): Promise<User> => {
    let url = `auth/signup`
    if (queryParams) {
        const queryString = new URLSearchParams(queryParams).toString()
        url = `${url}?${queryString}`
    }
    const {data} = await axiosV3.post(url, payload)
    return data
}

export const loginUser = async (
    payload: {authToken: string; rememberMe: boolean},
    queryParams?: QueryParamsType
): Promise<LoginResponseV3> => {
    let url = '/auth/login'
    if (queryParams) {
        const queryString = new URLSearchParams(queryParams).toString()
        url = `${url}?${queryString}`
    }
    const {data} = await axiosV3.post(url, payload)
    return data
    // LoginResponseV3Schema.parse(data) we will use it when BE will fix response
}
export const logoutUser = async (): Promise<{message?: string}> => {
    return await axiosV3.get('/auth/logout')
}

export const verifyUser = async (token: string): Promise<RequestMagicLink> => {
    const {data} = await axiosV3.get(`/auth/emails/verify`, {params: {token}})
    return data
}

export const getGendersV3 = async (isLeader?: boolean): Promise<GenderResponseV3> => {
    const {data} = await axiosV3.get(`/genders`, {params: {isLeader}})
    return data
}

export const getFeaturedCreators = async (
    responseType: 'extended' | 'small' = 'small'
): Promise<FeaturedCreatorsRequestType> => {
    const {data} = await axiosV3.get('/featured-creators', {params: {responseType}})
    return data
}

export const getFeaturedFollowers = async (): Promise<FeaturedFollowersRequestType> => {
    const {data} = await axiosV3.get('/featured-followers')
    return data
}
